/**
 * Per-page user guides shown in a hover popup on each page's description line.
 *
 * Guides are keyed by route path and resolved by {@link resolvePageGuide} (exact match,
 * then parent path), so pages don't need to pass anything — the PageHeader looks up the
 * guide for the current URL automatically. Content is written as an end-user guide:
 * what's on the page, how to use it, what its features do, and how changes ripple into
 * other areas of the PSA.
 */

export interface PageGuideSection {
  heading: string;
  /** Short intro paragraphs shown before the bullets (optional). */
  paragraphs?: string[];
  bullets?: string[];
}

export interface PageGuide {
  title: string;
  sections: PageGuideSection[];
}

/** The standard four headings every guide uses. */
const WHAT = 'What information is on this page';
const HOW = 'How to interact with it';
const FEATURES = 'Features & functions';
const IMPACT = 'How changes here affect other areas';

export const PAGE_GUIDES: Record<string, PageGuide> = {
  '/': {
    title: 'Home guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Your personal work-at-a-glance view. The cards you see depend on your functional roles (timesheet user, time approver, project manager, sales rep, technical/functional).',
        ],
        bullets: [
          'My current timesheet — your most recent draft week.',
          'Timesheets to approve — submitted weeks waiting on you.',
          'My open tickets — tickets assigned to you that are not closed.',
          'Projects I manage / projects I work on — active projects tied to you.',
          'My opportunities — deals where you are the sales rep.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click any row to open that record directly (ticket, timesheet, project, opportunity).',
          'Use the "All …" links in each card header to jump to the full list page.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Cards appear or hide automatically based on the roles set on your member record — there is nothing to configure here.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'This page is read-only; nothing you do here changes data. It simply reflects work recorded elsewhere in the PSA.',
        ],
      },
    ],
  },

  '/dashboards': {
    title: 'Dashboards guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Dashboards are canvases you build yourself out of cards. Each card is a saved question about PSA data — "how many", "which ones", or "how does it break down" — and it refreshes every time the dashboard is opened.',
        ],
        bullets: [
          'Personal dashboards are private to you. Shared dashboards are visible to everyone who has dashboard access.',
          'Metric cards show a single number, with optional colours when it crosses a threshold.',
          'List cards show a filtered table of records you can click straight through to.',
          'Bar and trend cards break the same records down by a field or by month.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Open a dashboard, then use Edit to add, move, resize or remove cards. Drag a card by its title bar; drag the bottom-right corner to resize it.',
          'Click a metric number, a bar or a point on a trend to see the records behind it, then click a row to open that record.',
          'Use Refresh to re-run every card. Ctrl-click (or Cmd-click) a row to open it in a new tab.',
          'Use "Set as home page" to land on a dashboard whenever you open the PSA. Home is still one click away in the sidebar.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Filters understand relative dates (overdue, this month, last 30 days), "is me" for anything assigned to you, empty/not-empty checks, and linked-record questions such as "has a project".',
          'Onboarding cards can filter on individual checklist steps — for example records where the customer form is completed but no project exists yet.',
          'Cards are always run as the person viewing them, so numbers respect each viewer\'s permissions.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Dashboards are read-only — building or deleting one never changes the underlying records.',
          'Creating or editing a SHARED dashboard requires Dashboards = Edit on one of your roles; personal dashboards need only view access.',
          'A card whose records you cannot normally see is hidden from you, so a shared dashboard never widens anyone\'s access.',
        ],
      },
    ],
  },

  '/sales/prospects': {
    title: 'Prospects guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Companies in your sales pipeline that are not yet delivery customers — leads and prospect accounts with their type, contacts, and activity.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a prospect to open its full record (same form as a customer).',
          'Use the search box to find a prospect by name, AKA, or NetSuite account name.',
          'Create a new prospect with the button in the top-right.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Prospects and customers share one record — the Type field (Prospect, Lead, Client…) determines which list they appear on.',
          'Contacts, opportunities, and notes can be attached to a prospect just like a customer.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Changing a company\'s Type moves it between the Prospects and Customers pages.',
          'Opportunities created for a prospect show on the Opportunities page and roll into the pipeline forecast.',
          'When a deal closes and onboarding completes, the same record becomes the billing customer — no re-entry needed.',
        ],
      },
    ],
  },

  '/sales/opportunities': {
    title: 'Opportunities guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Your sales pipeline: every deal from lead through close, with customer, sales rep, type, close date, products, and expected revenue. The Pipeline Forecast panel above the list spreads expected revenue across future months.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click an opportunity to open it — details, products, files, notes, CPQ quote link, and history.',
          'Sort any column by clicking its header; filter by type or status at the top.',
          'Expand the Pipeline Forecast panel to drill into a month and open the deals behind a number.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Products tab: attach catalog products with quantities, prices, and monthly/yearly billing frequency.',
          'CPQ tab: link a ConnectWise Sell quote and sync its products onto the opportunity.',
          'Files: drag-and-drop attachments (quote PDFs are attached automatically on CPQ sync).',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Setting status to "Order Approved" automatically creates an Onboarding record.',
          'Close date + product mix drive the Pipeline Forecast and the delivery Work Forecast.',
          'Products on the opportunity are copied to the agreement created during onboarding.',
        ],
      },
    ],
  },

  '/sales/quotes': {
    title: 'Quotes guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Every sales quote in the system. Each quote belongs to exactly one opportunity and is versioned (v1, v2, …) — only the LATEST version can be edited or sent; older versions are kept as read-only history. Opening a quote shows its twelve rich-text sections, the product-line snapshot, the version history, customer visit tracking, and (once approved) the customer\u2019s signature.',
        ],
        bullets: [
          'The list shows Customer, Opportunity, Type, Version (with a Latest badge), Status (Draft → Sent → Approved), and last update.',
          '"Latest versions only" is on by default — untick it to see superseded versions too.',
          'There is intentionally no "New quote" button here: quotes are created from the Quotes tab of an opportunity, which keeps every quote attached to a deal.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a row to open the quote editor.',
          'SECTIONS: a quote has twelve sections, each with its own editable title (leave it blank for no heading), a full rich-text body, and "page break before/after" checkboxes that control where the PDF starts a new page. Use the toolbar for bold/italic, lists, links, font, text size, color, and tables (insert a 3×3 table, then add/remove rows and columns from the toolbar while your cursor is inside it).',
          'PICTURES: paste a screenshot (Ctrl+V) or drag an image file straight into any section. It uploads to SharePoint automatically and stays with the quote. Drag the small round handle at the image\u2019s bottom-right corner to resize it; drag the image itself to move it.',
          'PRODUCT LINES: a quote has TWO line sections — e.g. a one-time install table and a monthly subscription table. Each section has its own billing frequency (One-time, Monthly, or Yearly) and its own placement in the document (before section 1 or after any section). Each table totals separately.',
          '"Apply template" copies a template\u2019s twelve sections and its product lines onto this quote AND adds those lines to the opportunity\u2019s Products tab (see the Quote Templates guide for how templates are built).',
          '"Send to customer" generates a private link (/q/…), lets you compose the email, and marks the quote Sent. You can copy, rotate (invalidate the old link), or disable the link at any time.',
          '"Download PDF" renders the quote — sections, formatting, images, page breaks, price table, and (after approval) the signature block.',
          '"New version" copies the current content into the next version; "Promote to latest" does the same from an older version. Delete removes a version (deleting the latest re-promotes the previous one).',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Customer visits panel: every open of the quote link is tracked with a timestamp, so you can see if and when the customer looked at it.',
          'On the public page the customer can tick/untick OPTIONAL lines and pick from BUNDLES (quantities and prices are locked), watch the totals update, then approve by typing their name and email and checking "I agree" — recorded with timestamp and IP.',
          'Approved quotes are frozen: the chosen lines are locked in, unpicked lines show "Not included", and no further edits are possible on that version.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Applying a template APPENDS its product lines to the opportunity\u2019s Products tab — review the opportunity afterwards to avoid duplicates.',
          'Customer approval automatically sets the opportunity status to Order Approved, which creates an Onboarding record and starts the delivery workflow.',
          'Approval also emails a confirmation to the signer, the opportunity\u2019s sales rep, and the PMO mailbox.',
          'Sending or rotating a link only affects that quote — older shared links stop working when you rotate.',
        ],
      },
    ],
  },

  '/sales/onboarding': {
    title: 'Onboarding guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'A checklist record for every new deal, created automatically when its opportunity is set to Order Approved. Each record tracks progress through the onboarding steps: MS Group, SPAM, LastPass, Welcome Email, customer form, PSA project, PSA agreement, and ClickUp project.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a record to open its checklist and work the steps in order.',
          'Steps that don\'t apply (e.g. returning customers) can be marked Skipped.',
          'Use the notes field to hand off context between team members.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'One-click actions per step: create the Microsoft 365 group, send the welcome email with the customer form link, create the PSA project/agreement, and create the ClickUp list from a template.',
          'The customer\'s completed welcome form fills billing address, contacts, and NetSuite details automatically.',
          'Delivery-phase labels (In Implementation, Ready to Bill, Live) show where each customer stands.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          '"Create PSA Agreement" builds a live agreement with lines copied from the opportunity — it will start appearing on Generate Invoices.',
          '"Create PSA Project" makes an active project ready for tickets and time.',
          'Contacts created via the welcome form become the customer\'s billing/project/support contacts used by invoices and reports.',
        ],
      },
    ],
  },

  '/customers': {
    title: 'Customers guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Every company you bill or deliver work for, with type, open opportunities, active projects, open tickets, and latest project/support time (LPT/LST).',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a customer to open the full form — Details, Contacts, Team, Projects, Tickets, Agreements, Opportunities, Time Entries, Billing, Invoices, Other, and History tabs.',
          'Search matches name, AKA, and NetSuite account name.',
          'Sort by any column header.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Contacts tab: manage people, their notification categories, portal access levels, and billing/project/support flags.',
          'Billing tab: QuickBooks ID and link, billing CC emails, net terms, billing reminder, and the default support agreement.',
          'Team tab: primary sales rep, PM, functional, and technical members for the account.',
          'Related tabs open the actual records with a "Back to customer" path.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Net terms set the due date on new invoices; billing CC emails receive invoice emails; the billing reminder is copied to each new invoice\'s internal notes.',
          'The default support agreement is attached to new support tickets for this customer, which controls how their ticket hours are billed.',
          'The QuickBooks ID is how invoices find (or create) the matching QBO customer when pushed.',
        ],
      },
    ],
  },

  '/contacts': {
    title: 'Contacts guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Every customer contact in the PSA in one list, with the company they belong to, their title, email, phone, and portal access levels.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Filter by customer, search name/email/title, and switch between active, inactive, and all contacts.',
          'Click a row to edit the contact; use "New contact" to add one.',
          'A contact must belong to a customer, so the Customer field is required when adding and locked afterwards.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Set billing, project, and support roles plus Support Ticket / AI / Admin access levels.',
          'Choose the service categories a contact is notified about, and flag Catalyst+ overage recipients.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Billing contacts receive invoice emails; support contacts and ticket managers drive customer portal access.',
          'These are the same records shown on the customer form\'s Contacts tab — editing here changes them everywhere.',
        ],
      },
    ],
  },

  '/delivery/work-forecast': {
    title: 'Work Forecast guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'A count of open opportunities (upcoming projects) grouped by type and expected close month — a heads-up for the delivery team about how much work is coming and when.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Read the grid: each cell is a count of open deals of that type expected to close that month.',
          'Drill into a cell to see the underlying opportunities (rows are intentionally not clickable — delivery members may not have sales access).',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Counts come straight from open opportunities — one count in the close month, no revenue shown.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Read-only. To change what appears here, update the opportunity\'s type, status, or close date on the Opportunities page.',
        ],
      },
    ],
  },

  '/projects': {
    title: 'Projects guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'All delivery engagements with customer, category, PM, dates, budget vs actual hours, and % of budget used. The Project Revenue Forecast panel spreads project revenue across future months.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a project to open its detail page — phases, tickets, products, and the public time-entry link.',
          'Quick-edit PM, start date, and end date directly in the list.',
          'Use the "My projects" checkbox to see only projects where you\'re assigned to a ticket.',
          'End-date and budget-% badges turn yellow/red as projects approach or pass their limits.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Phases organize tickets and can override service category, billing class, and rate.',
          'The public project link lets assigned members log time from outside the app.',
          'A project can be linked to a managed-services agreement so its time consumes that agreement\'s prepaid hours instead of normal project billing.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Budget hours, rates, and billing classes here determine what project invoices look like on Generate Invoices.',
          'The default service category/billing class flow down to phases, tickets, and time entries unless overridden.',
          'Closing a project from the public link can flip its onboarding record to "Ready to Bill".',
        ],
      },
    ],
  },

  '/tickets': {
    title: 'Tickets guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Support and project tickets in one list: ID, title, customer, type, status, priority, assignees, logged hours, and created/updated/closed dates. Support and project tickets are permissioned separately — you may see one kind or both.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a ticket to open it — details, assignees, hours list, public link, and technical history.',
          'Filter by customer, support/project, category, status, or assignee; search by title, customer, reference, or number.',
          'Sort any column, including latest-time-entry (LTT).',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Support tickets link to a customer and (optionally) an agreement; project tickets belong to a project phase.',
          'Support tickets sync two-way with ClickUp: status, assignees, and list moves flow in automatically, and the ticket ID is the ClickUp custom ID (e.g. CST-4021).',
          'Each ticket has a public link a customer can view without signing in.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Hours logged on a ticket appear on timesheets and become billable lines on ticket, project, or managed-services invoices depending on the linked agreement/project.',
          'The ticket type sets the default service category and billing class for its time — which sets the rate.',
          'For ClickUp-synced tickets, status/assignee changes should be made in ClickUp; the integration keeps PSA in step.',
        ],
      },
    ],
  },

  '/time-entries': {
    title: 'Time Entries guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Every logged unit of labor: date, member, hours, customer, ticket/project, agreement, service category, billing class, rate, and billing state (invoiced, written off, billed externally).',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click an entry to open and edit it (work date, hours, notes are required; billing fields are system-set).',
          'Filter by member, customer, project, or date range.',
          'Create entries here, on a ticket, on your timesheet, or via the public project link.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Rates resolve automatically from the agreement/service-category rate table, phase overrides, or billing class.',
          'Entries roll up into the member\'s weekly timesheet automatically.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Billable, un-invoiced entries are exactly what Generate Invoices picks up — editing hours, dates, or billing class changes upcoming invoices.',
          'Entries on managed-services work draw down the agreement\'s included hours (Cat+ Usage).',
          'Changing an entry after its week is submitted affects that timesheet\'s totals and approvals.',
        ],
      },
    ],
  },

  '/timesheets': {
    title: 'Timesheets guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Weekly rollups of your time entries with status (draft, submitted, approved), totals, and on-time/late submission tracking against the global due day.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a week to open the grid — log time by day against your assigned tickets and tasks.',
          '"Load my open tickets" pre-fills rows for everything assigned to you; empty rows are cleaned up on submit.',
          'Click the summary boxes (billable, by customer, by charge code…) to filter the grid to just those entries.',
          'Submit when the week is complete; it then goes to your approver(s) in order.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Copy last week\'s tasks (with or without hours) to start a new week fast.',
          'Tiered approvals: each approver is emailed in turn and can approve straight from the email.',
          'Submissions are stamped on-time or past-due based on the configured weekly due date.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Hours you log here are the same time entries that get billed to customers — accuracy directly affects invoices.',
          'Late submissions can forfeit hours-based bonus for that week on the Monthly Team Bonus report.',
          'Approved timesheets lock; an admin can reopen one back to draft if a correction is needed.',
        ],
      },
    ],
  },

  '/approvals': {
    title: 'Approvals guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Submitted weekly timesheets waiting for review, with member, week, totals, and submission timing.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a timesheet to review its grid, summaries, and raw entry list.',
          'Approve it, or un-submit it back to the member with corrections needed.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Only members flagged as time approvers (or with delete-level time access) can act here.',
          'With tiered approvals, the sheet moves to the next approver after you; the member is emailed on final approval.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Approval finalizes the week\'s hours as ready for billing.',
          'Un-submitting returns the timesheet to draft so the member can fix entries — totals and billing then update accordingly.',
        ],
      },
    ],
  },

  '/agreements': {
    title: 'Agreements guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Recurring billing arrangements per customer: product subscriptions, managed services (prepaid hour blocks), pay-as-you-go support, and project agreements — with status, dates, and invoice schedule.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click an agreement to open it — lines, service-category rates, time entries, tickets, invoices, and (for managed services) Cat+ Usage.',
          'Filter by type or status; only Active agreements are billed and reported.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Lines carry quantity, price, monthly/yearly frequency, start/end dates, and billed-through tracking.',
          'Managed services: included hours, rollover, overage rate, and a live usage view with a shareable public link.',
          '"Generate prorate invoice" bills a partial first month and sets prorated first-month included hours automatically.',
          'Rate increase date/percent schedule future price changes.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Everything on Generate Invoices for subscriptions, managed services, and ticket billing flows from agreement lines, rates, and dates.',
          'Status matters: draft agreements do not bill and do not appear on customer reports — set Active to go live.',
          'The rate table here sets the hourly rate applied to every time entry under the agreement.',
        ],
      },
    ],
  },

  '/invoices': {
    title: 'Invoices guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Every invoice — PSA-generated and externally imported — with number, customer, source, category, status, dates, and totals.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click an invoice to review lines and time entries, edit while draft, download the PDF, or send it.',
          'Filter by status, customer, or source; export the filtered list to CSV.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Lifecycle: draft → in review → sent → closed (void at any point).',
          'Hour-backed invoices list each time entry with included/overage flags and a write-off button.',
          'The QuickBooks link field jumps straight to the synced QBO invoice.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Voiding an invoice releases its time entries and subscription periods to be billed again.',
          'Writing off a time entry removes it from every future invoice (without changing its billable flag).',
          'Sending marks lines/hours as billed, which updates billed-through dates and the billing audit.',
        ],
      },
    ],
  },

  '/invoices/generate': {
    title: 'Generate Invoices guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'On-demand invoice creation from four sources — Product Subscriptions, Managed Services, Projects, and Tickets — showing everything billable as of your chosen Thru Date, with per-tab and overall totals.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Pick a Thru Date, review each tab, click a row to preview its lines/hours, then generate.',
          'Filter by customer; click a source or customer name to open it (Ctrl+click for a new tab).',
          'Toggles show/hide QBO class and monthly/yearly banners; date-range options control which historical hours are included.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Managed services bills the previous month\'s hours against included hours (rollover and first-month proration respected) and bills only the overage.',
          'Subscriptions respect line start/end dates and billed-through — nothing double-bills.',
          'In-row tools: recompute rates and write off hours before invoicing.',
          'A red banner warns when a line has no QBO class (fix before pushing to QuickBooks).',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Generating creates a draft invoice on the Invoices page and stamps its hours/periods as billed.',
          'The Last Invoice column and agreement billed-through dates update as you generate.',
          'What appears here is driven by agreements, projects, and time entries — fix source data there, not here.',
        ],
      },
    ],
  },

  '/invoices/qbo-batch': {
    title: 'Send to QuickBooks guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Draft and in-review invoices ready to push to QuickBooks Online, individually or as a batch, with a detailed processing log.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Select invoices and push; optionally email each to the customer\'s billing contacts at the same time.',
          'Review the per-invoice log after each run to confirm success or see exactly what failed.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Customers match by the QuickBooks ID on the customer record; missing customers are created in QBO and the new ID written back to PSA.',
          'Cross-links are set both ways: PSA stores the QBO invoice link, and the QBO invoice stores the PSA URL.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Pushed invoices are marked sent to QuickBooks; QuickBooks becomes the source of truth for payment status.',
          'Lines missing a QBO class will fail to push — assign classes on products/billing setup first.',
        ],
      },
    ],
  },

  '/billing-audit': {
    title: 'Billing Audit guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'The raw billing-control data behind on-demand invoicing, in three tabs: recurring agreement lines (billed-through), managed-services agreements (hour banks), and billable time entries (invoice state, external references).',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Filter by customer or agreement to isolate a billing question.',
          'Hand-correct fields inline: billed-through dates, rollover banks, written-off and billed-externally flags.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Shows external invoice references and dates for time imported from ConnectWise.',
          'Robust filtering across the full time-entry history.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'These fields directly control Generate Invoices: moving a billed-through date back makes periods billable again; writing off an hour hides it from all invoices.',
          'Use with care — this page edits the same records the invoice engine reads.',
        ],
      },
    ],
  },

  '/products': {
    title: 'Products guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'The catalog of products and services you sell: name, price, service category, PSA class (Agreement vs Service), and QBO mapping.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a product to edit it and see which agreements include it as a line.',
          'Search and sort to audit the catalog.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'PSA class distinguishes recurring agreement items from one-time service revenue.',
          'The service category ties a product to forecasting and invoice categorization.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Products are referenced by opportunity lines, agreement lines, and project fixed-fee billing — a price or category change affects future quotes and invoices (existing lines keep their captured prices).',
          'The product\'s service category drives the invoice header category and revenue reporting.',
        ],
      },
    ],
  },

  '/service-categories': {
    title: 'Service Categories guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Billable labor types (Functional, Technical, Exec…) and their default hourly rates.',
        ],
      },
      {
        heading: HOW,
        bullets: ['Add, rename, or re-rate categories; changes apply wherever no override exists.'],
      },
      {
        heading: FEATURES,
        bullets: [
          'Default rates here are the base of the rate cascade: agreement rate table → phase override → category default.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Rate changes affect newly logged/recomputed time, not already-billed hours.',
          'Categories are used across tickets, projects, products, member bonus configuration, contact notification lists, and invoice descriptions (e.g. "Functional Time @ 175.00/hr").',
        ],
      },
    ],
  },

  '/billing-classes': {
    title: 'Billing Classes guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Rules for how logged hours are billed and shown on invoices — e.g. normal billable, Fixed Fee Project Hour, No Charge, BugFix, No Bill.',
        ],
      },
      {
        heading: HOW,
        bullets: ['Create or edit classes; assign them on tickets, phases, projects, or individual time entries.'],
      },
      {
        heading: FEATURES,
        bullets: [
          'A class can zero-out or override the rate on hours it\'s applied to.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Billing class determines whether an hour produces invoice revenue.',
          'BugFix / No Charge / No Bill hours feed the bonus report\'s no-charge reduction.',
        ],
      },
    ],
  },

  '/members': {
    title: 'Members guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'People who sign in with Entra ID: their roles (RBAC), functional flags (PM, sales rep, technical, functional, timesheet user, time approver), approvers, bonus settings, and last-login/online status.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click a member to edit their roles, flags, timesheet approvers, and bonus configuration.',
          'Deactivate a member to remove access while keeping their history.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Functional flags drive dashboard cards and filter dropdowns (PM lists, sales-rep lists, customer team pickers).',
          'Bonus fields (threshold, % of product revenue, categories, subtypes, Active PIP) feed the Monthly Team Bonus report.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Roles assigned here control everything a member can see and do (see the Roles page).',
          'Timesheet approver order defines the tiered approval chain for that member\'s weeks.',
        ],
      },
    ],
  },

  '/roles': {
    title: 'Roles guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Permission sets built from per-menu-item grants. Each menu item can be None, View, Edit, or Delete; a member\'s effective access is the most permissive across all their roles.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Create a role, set a level per menu item, then assign the role on the Members page.',
          'Support Tickets and Project Tickets are separate grants — a role can, for example, edit project tickets but only view (or not see) support tickets.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'View shows the menu and data; Edit allows changes; Delete allows removal.',
          'Some admin areas (members, roles, integrations) are admin-only regardless of role grants.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Changes apply to every member holding the role at their next request — no re-login needed.',
          'Removing a grant hides the menu item and blocks the underlying data APIs, including tabs on the customer form.',
        ],
      },
    ],
  },

  '/api-users': {
    title: 'API Users guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Service accounts with API keys for programmatic access to the PSA REST API, each with its own roles.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Create an API user, copy its key immediately (it is shown once), and assign roles to scope what it can access.',
          'The page shows the base URL and a link to the API documentation.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Keys use the same RBAC as members, so an integration only reaches the areas its roles allow.',
          'Keys can be revoked at any time.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'External systems using a key see live PSA data; changing its roles immediately changes what they can read or write.',
        ],
      },
    ],
  },

  '/integrations/excel-access': {
    title: 'Excel Access guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Read-only OData feed credentials so individuals can connect Excel or Power BI directly to PSA data with native refresh.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Grant access to a person, share the feed URL, and have them use "From OData Feed" with Basic auth in Excel/Power BI.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Server-side filtering and paging; credentials live outside the workbook.',
          'The feed respects the grantee\'s permissions — they only see entity sets (customers, projects, tickets, time, invoices, agreements, opportunities) their access allows.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Read-only — nothing built on the feed can change PSA data. Revoking access breaks refresh for that person immediately.',
        ],
      },
    ],
  },

  '/admin/ticket-types': {
    title: 'Ticket Types guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Support-ticket categories with their default service category and billing class, organized into groups.',
        ],
      },
      {
        heading: HOW,
        bullets: ['Create or edit types; assign each a group, default service category, and billing class.'],
      },
      {
        heading: FEATURES,
        bullets: [
          'Each ClickUp list maps to a ticket type — moving a task between lists in ClickUp re-types the PSA support ticket automatically.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'The type\'s defaults set the service category and billing class (and therefore the rate) on time logged to its tickets.',
          'Types appear as the category filter on the Tickets page and on ticket invoices.',
        ],
      },
    ],
  },

  '/admin/ticket-type-groups': {
    title: 'Ticket Groups guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['Groups that organize ticket types (e.g. Support, Onboarding) for cleaner filtering.'],
      },
      {
        heading: HOW,
        bullets: ['Create groups here, then assign them on the Ticket Types page.'],
      },
      {
        heading: FEATURES,
        bullets: ['Groups power the group filter on the Tickets page.'],
      },
      {
        heading: IMPACT,
        bullets: ['Purely organizational — no effect on billing or rates.'],
      },
    ],
  },

  '/admin/ticket-statuses': {
    title: 'Ticket Statuses guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The global, ordered list of ticket statuses, each flagged open or closed.'],
      },
      {
        heading: HOW,
        bullets: ['Add, reorder, or edit statuses; mark which ones count as "closed".'],
      },
      {
        heading: FEATURES,
        bullets: [
          'ClickUp task statuses map to these statuses on the ClickUp integration page, keeping both systems in step.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'The closed flag drives open-ticket counts, filters, dashboards, customer reports, and the ticket\'s closed date stamp.',
        ],
      },
    ],
  },

  '/admin/customer-types': {
    title: 'Customer Types guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The configurable list of customer types (Client, Prospect, Lead, Catalyst+, O365…).'],
      },
      {
        heading: HOW,
        bullets: ['Add or reorder types; assign them (multi-select) on each customer\'s Details tab.'],
      },
      {
        heading: FEATURES,
        bullets: ['Types are labels — a customer can hold several at once.'],
      },
      {
        heading: IMPACT,
        bullets: [
          'Types decide whether a company appears on the Prospects or Customers page and power the Type column and filters.',
        ],
      },
    ],
  },

  '/admin/agreement-subtypes': {
    title: 'Agreement Subtypes guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['A descriptive list of agreement subtypes (Gold, VAR Connect…). They do not affect billing math.'],
      },
      {
        heading: HOW,
        bullets: ['Maintain the list here; pick a subtype on each agreement.'],
      },
      {
        heading: FEATURES,
        bullets: ['Subtypes slice the agreement forecast and label agreements in lists.'],
      },
      {
        heading: IMPACT,
        bullets: [
          'Members\' bonus configuration matches on subtypes — product-revenue bonus and cancellation reductions on the bonus report follow these labels.',
        ],
      },
    ],
  },

  '/admin/invoice-templates': {
    title: 'Invoice Templates guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The PDF layout used for each kind of generated invoice (subscriptions, managed services, projects, tickets).'],
      },
      {
        heading: HOW,
        bullets: ['Edit a template\'s HTML and preview it; tokens fill in customer and line data at render time.'],
      },
      {
        heading: FEATURES,
        bullets: [
          'Templates include the payment footer (ACH/check details, W-9 link) and hour-detail sections with included/overage flags.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Changes apply to every future PDF of that invoice type — existing sent PDFs are unaffected until re-rendered.',
        ],
      },
    ],
  },

  '/admin/quote-templates': {
    title: 'Quote Templates guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Reusable starting points for quotes. A template holds the same twelve rich-text sections as a quote plus a product-line list. When a sales rep clicks "Apply template" on a quote, the sections are copied onto the quote and the lines are copied to BOTH the quote and the opportunity\u2019s Products tab.',
        ],
        bullets: [
          'The list shows each template\u2019s name, the Opportunity Type it belongs to, whether it is Active, and when it was last updated.',
          'A template tied to an Opportunity Type is only offered on quotes whose opportunity has that Type; a template with "Any type" is offered everywhere.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Click "New template", give it a name and (optionally) an Opportunity Type, then build it out on the editor page.',
          'SECTIONS: write the boilerplate content once — intro, scope, terms, etc. Each section has an editable title, full rich-text formatting (fonts, sizes, colors, tables), and page-break checkboxes that carry through to the quote PDF. Paste or drag pictures straight into a section body.',
          'LINES: there are TWO line sections (e.g. one-time install charges and a monthly subscription), each with its own billing frequency (One-time/Monthly/Yearly) and its own placement in the document. Click "Add line" in a section, pick a product (this pre-fills the description and unit price — both stay editable), and set the quantity. The order of the rows is the order they appear on the quote.',
          'Untick "Active" to retire a template without deleting it — it disappears from the apply-template picker but keeps its content.',
        ],
      },
      {
        heading: 'Optional lines, bundles & min choose',
        paragraphs: [
          'These three line fields control what the CUSTOMER can choose on the delivered quote. Quantities, descriptions, and prices are always locked — customers only decide whether a line is included.',
        ],
        bullets: [
          'OPTIONAL (checkbox): the customer may add or remove this line. It starts unticked on their page and is excluded from totals until they pick it.',
          'BUNDLE KEY (text): lines that share the same key form a choose-from group. Use any short label ("A", "Backup", "Training").',
          'MIN CHOOSE (number): how many lines from that bundle the customer MUST pick before they can approve. If lines in a group disagree, the highest number wins.',
          'Leave all three blank for a required line that is always included and always in the totals.',
          'EXAMPLE — simple add-on: "Extra training day, $1,500" with Optional ticked → the customer can add it; if they do, it joins the totals and the signed agreement.',
          'EXAMPLE — pick one of three: three support tiers (Bronze/Silver/Gold) all with Bundle key "SUPPORT" and Min choose 1 → the customer must pick at least one tier to approve; the others show "Not included".',
          'EXAMPLE — pick two of four: four workshop topics with Bundle key "WS" and Min choose 2 → approval is blocked until at least two are selected.',
          'RESULT after approval: the customer\u2019s picks are frozen onto the quote, only the selected lines appear in the totals and PDF, and the signature block records who approved and when.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Applying a template ADDS its lines to the opportunity\u2019s products (lines whose product is an agreement type are flagged recurring) — this feeds the pipeline forecast and the agreement created during onboarding.',
          'Editing a template only affects FUTURE applies; quotes that already used it keep their copied content.',
          'Deleting a template never touches existing quotes.',
          'Templates are admin-managed; sales users see them only in the apply-template picker.',
        ],
      },
    ],
  },

  '/admin/email-settings': {
    title: 'Email Settings guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Which mailbox PSA sends each kind of email from (invoices, reports, approvals, welcome emails…). The default applies unless a function has its own sender.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Set the default From mailbox and per-function overrides; send a test email to verify.',
          'Set the "Ready to Bill notification email" to the mailboxes that should hear when a project is closed and ready to bill (comma-separated; blank disables it).',
        ],
      },
      {
        heading: FEATURES,
        bullets: ['Sending uses the Microsoft Graph credentials configured on the Microsoft Graph page.'],
      },
      {
        heading: IMPACT,
        bullets: [
          'Every automated email in the system — invoices, customer reports, timesheet reminders, notifications — uses these senders.',
        ],
      },
    ],
  },

  '/admin/microsoft-graph': {
    title: 'Microsoft Graph guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The Entra application credentials PSA uses for outgoing email and SharePoint file storage.'],
      },
      {
        heading: HOW,
        bullets: ['Enter/rotate the client credentials and test connectivity.'],
      },
      {
        heading: FEATURES,
        bullets: ['One credential set powers email sending, file attachments (SharePoint), and MS group creation during onboarding.'],
      },
      {
        heading: IMPACT,
        bullets: [
          'If these credentials expire, all outgoing email and file features stop working — rotate them here to restore service.',
        ],
      },
    ],
  },

  '/integrations/qbo': {
    title: 'QuickBooks guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The connection between PSA and your QuickBooks Online company, plus sync settings (dry-run mode, class mapping).'],
      },
      {
        heading: HOW,
        bullets: ['Connect/reauthorize via the QuickBooks sign-in popup; test the connection; review sync settings.'],
      },
      {
        heading: FEATURES,
        bullets: [
          'Customer matching by QuickBooks ID with automatic creation and write-back.',
          'Dry-run mode lets you rehearse pushes without writing to QBO.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'This connection powers the Send to QuickBooks page — if it breaks, invoice pushing stops until reauthorized.',
        ],
      },
    ],
  },

  '/integrations/connectwise': {
    title: 'ConnectWise guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'The read-only connector that mirrors ConnectWise records into PSA during the transition: customers, agreements, projects, tickets, time, timesheets, and invoices, each as a configurable dataset with mappings and schedules.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Run a dataset sync manually or on its schedule; review per-run logs.',
          'Configure field mappings and aliases (statuses, types, work roles) per dataset before syncing.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Sequential sync scheduling runs datasets in a defined order.',
          'Special handling: billed-through from last CW invoice, written-off time, agreement status overrides.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Synced records land in the same tables you use everywhere — synced time shows on timesheets, synced agreements bill on Generate Invoices.',
          'Data synced from ConnectWise is not audit-logged as user changes; it can also overwrite manual edits on the next sync.',
        ],
      },
    ],
  },

  '/integrations/clickup': {
    title: 'ClickUp guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Webhook credentials, mapping tables, and activity logs for the two-way ClickUp ↔ PSA support-ticket integration.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Maintain the mappings: ClickUp list → PSA ticket type, ClickUp member → PSA member, ClickUp status → PSA status, and opportunity type → space/folder/list template.',
          'Use "Refresh workspace data" after ClickUp structure changes; watch the activity log to trace each webhook.',
          'Backfill buttons (batched, resumable) fill the PSA Ticket Link on existing ClickUp tasks.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Task created in a mapped list → support ticket created/updated in PSA (customer matched by NetSuite Account ID + contact email).',
          'Status, assignee, and list-move changes flow to the PSA ticket automatically; a list move re-types the ticket.',
          'The ClickUp custom ID (e.g. CST-4021) is the ticket\'s reference ID everywhere in PSA.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Mapping changes affect every future webhook — an unmapped list stops creating tickets.',
          'Because ClickUp drives ticket status/assignees, changes made directly in PSA can be overwritten by the next ClickUp event.',
        ],
      },
    ],
  },

  '/integrations/catalyst-portal': {
    title: 'Catalyst Portal guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The connection to the Catalyst public API (api.nscatalyst.com) with a browser for its read-only endpoints.'],
      },
      {
        heading: HOW,
        bullets: ['Store the API key, test connectivity, and browse endpoint data.'],
      },
      {
        heading: FEATURES,
        bullets: ['Used to pull portal data such as NetSuite account names onto PSA customers.'],
      },
      {
        heading: IMPACT,
        bullets: ['Read-only exploration today; the stored key enables portal-driven enrichment features.'],
      },
    ],
  },

  '/integrations/connectwise-cpq': {
    title: 'ConnectWise CPQ guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['The read-only connection to ConnectWise Sell (CPQ) used to link quotes to opportunities.'],
      },
      {
        heading: HOW,
        bullets: [
          'Configure the connection here; on an opportunity\'s CPQ tab, link a quote and click "Sync from CPQ".',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Sync copies quote products onto the opportunity (with monthly/yearly frequency from the tab\'s recurring option), attaches the quote PDF, and can set the opportunity to Order Approved when the quote is approved.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Sync overwrites the opportunity\'s products with the quote contents.',
          'Setting Order Approved kicks off onboarding automatically.',
        ],
      },
    ],
  },

  '/admin/ai-assistant': {
    title: 'AI Assistant guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['Settings for the read-only "Ask PSA" assistant (Anthropic Claude): API key, enablement, and options.'],
      },
      {
        heading: HOW,
        bullets: ['Paste the Anthropic key (stored encrypted) and flip Enable — the Ask PSA button then appears in the top bar for everyone.'],
      },
      {
        heading: FEATURES,
        bullets: ['Answers are scoped to each person\'s own permissions — the assistant cannot show data the user couldn\'t open themselves.'],
      },
      {
        heading: IMPACT,
        bullets: ['Read-only: the assistant can answer questions but never changes records. Disabling removes the button immediately.'],
      },
    ],
  },

  '/reports': {
    title: 'Internal Reports guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Saved internal reports with schedules, recipients, and run history — plus the Monthly Team Bonus report.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Run any report on demand ("Send now") or set a schedule (multiple times daily, weekend skipping, skip-if-empty).',
          'Watch the last-run status/error per report; a banner warns if scheduled delivery stalls.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Reports email their results to the configured recipients and can be downloaded.',
          'The bonus report breaks down hours bonus, product sales, no-charge reductions, and cancellations per member for the previous month.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Reports are read-only over PSA data. The bonus report reads member bonus settings, timesheet timing, invoices, and billing classes — accuracy depends on those being right.',
        ],
      },
    ],
  },

  '/reports/bonus': {
    title: 'Monthly Team Bonus guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'The previous calendar month\'s bonus per member, grouped into four sections: hours over threshold, product sales, no-charge hour reductions, and agreement cancellations.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Review each member\'s rows; admins can toggle a week\'s On time/Late flag inline where the synced timing was wrong.',
          'Download the PDF or email it to recipients.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Hours bonus pays $10/hour over the member\'s weekly threshold — forfeited for that week if the timesheet was late.',
          'Product revenue and cancellations match invoices to the member\'s agreement subtypes at their % of product revenue.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Numbers come from member bonus fields, timesheets, invoices, and billing classes — corrections belong on those records; this page only reads (except the timing toggle).',
        ],
      },
    ],
  },

  '/customer-reports': {
    title: 'Customer Reports guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Weekly customer-facing status reports — one filtered email per enrolled customer covering open projects, ticket activity, managed-services hours (with first-month proration), and pay-as-you-go usage.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Enroll customers and pick which of their contacts (and internal members) receive each report.',
          'Toggle report sections on/off per report; edit the HTML template if needed.',
          'Use Test Mode to send every customer\'s report to your own inbox instead of the customers.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Automatic weekly schedule plus a Send Now button.',
          'Only active agreements and projects appear; managed-services hours match the customer\'s invoice exactly, including prorated first months.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'These emails go to customers — enrollment and recipient changes take effect on the next send.',
          'Content reflects live PSA data at send time: ticket statuses, project activity, and agreement hours.',
        ],
      },
    ],
  },

  '/insights/studio': {
    title: 'Insights Studio guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'AI-driven analysis of time entry data. Ask questions in plain English — who worked on what, when, for how long, billable mix, revenue — and get a narrative answer with interactive charts.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Type a question or click a suggested chip. The AI runs safe, read-only queries and answers with charts.',
          'Click any bar, slice, or table row to see the underlying time entries; click an entry to open it.',
          'Use the Data toggle on a card to switch between the chart and the numbers.',
          'The Member lens tab profiles one member: an hours heatmap calendar, customer and category mix, and a monthly billable trend.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Dimensions: member, customer, project, agreement, ticket, service category, billing class, billable, and day/week/month/quarter/year buckets.',
          'Measures: hours, billable/non-billable hours, revenue (billable hours × rate), entry counts, and average rate.',
          'Every answer ends with suggested follow-up questions to keep the analysis going.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Strictly read-only — nothing here changes data. Access is granted per role via the Insights Studio menu item because it reads time data across all members.',
        ],
      },
    ],
  },

  '/insights/forecast': {
    title: 'Forecast guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'The combined future-revenue picture: a month-by-month grid summing the Pipeline (opportunities), Agreements, Projects, and Tickets forecasts, with each section broken out below.',
        ],
      },
      {
        heading: HOW,
        bullets: [
          'Expand a section and drill into any month\'s number to see the records behind it.',
          'Toggle draft vs active agreements, and remaining-budget vs un-billed-hours project revenue.',
          'Generate a public, tokenized read-only link to share the forecast outside the app.',
        ],
      },
      {
        heading: FEATURES,
        bullets: [
          'Pipeline spreads opportunity revenue using project/agreement length; agreements project recurring revenue; projects spread budget; tickets forecast un-billed support hours net of included blocks.',
        ],
      },
      {
        heading: IMPACT,
        bullets: [
          'Read-only. To move a number, change the source record — close dates, agreement lines, project budgets, or time entries.',
        ],
      },
    ],
  },

  '/insights/notifications': {
    title: 'Notifications guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['Automatic email watchers that fire when records change — e.g. a new unassigned ticket.'],
      },
      {
        heading: HOW,
        bullets: ['Create a notification: pick the entity, the condition, and the recipients. Disable or delete it anytime.'],
      },
      {
        heading: FEATURES,
        bullets: ['Watchers run on a schedule every few minutes and email through the configured mail settings.'],
      },
      {
        heading: IMPACT,
        bullets: ['Notifications only send email — they never change the records they watch.'],
      },
    ],
  },

  '/admin/maintenance': {
    title: 'Maintenance mode guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: ['A switch that temporarily blocks changes during a deploy so no user write lands mid-update.'],
      },
      {
        heading: HOW,
        bullets: ['Turn maintenance on before risky deploys and off after; users see a banner and writes are rejected while on.'],
      },
      {
        heading: FEATURES,
        bullets: ['Admins retain enough access to manage the switch itself.'],
      },
      {
        heading: IMPACT,
        bullets: ['While on, everyone\'s saves fail with a clear message — coordinate timing with the team.'],
      },
    ],
  },

  '/admin/snapshot': {
    title: 'Snapshot Management guide',
    sections: [
      {
        heading: WHAT,
        paragraphs: [
          'Controls for the disposable sandbox environment: refresh its data from production, redeploy its build, and open or close it to users.',
        ],
      },
      {
        heading: HOW,
        bullets: ['Trigger refresh/redeploy actions (they run in GitHub Actions) and watch run history here.'],
      },
      {
        heading: FEATURES,
        bullets: ['The sandbox is a full copy on its own URL — testers can change anything without touching production.'],
      },
      {
        heading: IMPACT,
        bullets: ['Refreshing the sandbox overwrites all sandbox data with a fresh production copy. Production is never modified from here.'],
      },
    ],
  },
};

/**
 * Resolves the guide for a pathname: exact match first, then walk up parent paths so
 * detail routes (e.g. /projects/123) inherit their list page's guide. The root guide
 * ('/') is only used for the dashboard itself, never as a fallback for other pages.
 */
export function resolvePageGuide(pathname: string): PageGuide | null {
  let path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return PAGE_GUIDES['/'] ?? null;
  for (;;) {
    const guide = PAGE_GUIDES[path];
    if (guide) return guide;
    const slash = path.lastIndexOf('/');
    if (slash <= 0) return null;
    path = path.slice(0, slash);
  }
}
